import { IRowNode, MenuItemDef } from "ag-grid-community";
import { RestoreType } from "../../store/dependencyHandlers";

export interface ContextMenuState {
  canUpdate: boolean;
  canRestore: boolean;
  rowSelected: boolean;
  rowSelectedLDV: boolean;
  rowSelectedADV: boolean;
  rowSelectedADVDep: boolean;
  rowSelectedDM: boolean;
  /** Selected rows with no metaData.Client; Force Client is disabled while above 0. */
  rowsSelectedMissingClient: number;
  /** Selected rows with no metaData.ClientAgent; Force ClientAgent is disabled while above 0. */
  rowsSelectedMissingClientAgent: number;
  /** Set when group Force is unavailable for the right-clicked group; disables both items. */
  groupForceBlockedReason: string | undefined;
}

const missingTargetTooltip = (count: number, target: string): string =>
  `${count} selected ${count === 1 ? 'row has' : 'rows have'} no ${target} — deselect ${count === 1 ? 'it' : 'them'} to force`;

export interface ContextMenuHandlers {
  getGroupKeysFromNode: (node: IRowNode) => Record<string, string>;
  onCheckIsReadyForGroup: (keys: Record<string, string>) => void;
  onForceClientForGroup: (keys: Record<string, string>) => void;
  onForceClientAgentForGroup: (keys: Record<string, string>) => void;
  onRestoreGroupByScheme: (keys: Record<string, string>) => void;
  onRestoreGroupByClient: (keys: Record<string, string>) => void;
  onRestoreGroupByClientAgent: (keys: Record<string, string>) => void;
  onCheckExistsClick: () => void;
  onAbandonClick: () => void;
  onForceClientClick: () => void;
  onForceClientAgentClick: () => void;
  onRestoreSchemePendingDependenciesClick: (type: RestoreType) => void;
  onRestoreClientPendingDependenciesClick: (type: RestoreType) => void;
  onRestoreClientAgentPendingDependenciesClick: (type: RestoreType) => void;
  onRestoreEnvPendingDependenciesClick: (type: RestoreType) => void;
  onExportGroupCsvClick: (keys: Record<string, string>) => void;
  onExportCsvClick: () => void;
}

export function buildContextMenuItems(
  params: { node?: IRowNode | null },
  state: ContextMenuState,
  handlers: ContextMenuHandlers
): (string | MenuItemDef)[] {
  const {
    canUpdate, canRestore, rowSelected,
    rowSelectedLDV, rowSelectedADV, rowSelectedADVDep, rowSelectedDM,
    rowsSelectedMissingClient, rowsSelectedMissingClientAgent, groupForceBlockedReason,
  } = state;
  const {
    getGroupKeysFromNode,
    onCheckIsReadyForGroup, onForceClientForGroup, onForceClientAgentForGroup, onRestoreGroupByScheme, onRestoreGroupByClient, onRestoreGroupByClientAgent,
    onCheckExistsClick, onAbandonClick, onForceClientClick, onForceClientAgentClick,
    onRestoreSchemePendingDependenciesClick, onRestoreClientPendingDependenciesClick,
    onRestoreClientAgentPendingDependenciesClick, onRestoreEnvPendingDependenciesClick,
    onExportGroupCsvClick, onExportCsvClick,
  } = handlers;

  const result: (string | MenuItemDef)[] = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator'];

  // Group-level actions when right-clicking on a group row
  if (params.node?.group) {
    const groupKeys = getGroupKeysFromNode(params.node);
    // Use the server-provided childCount from the GROUP BY query result.
    // allChildrenCount is null in ag-grid v28+ SSRM for groups with unloaded children.
    const childCount = (params.node.data?.childCount as number) ?? params.node.allChildrenCount ?? 0;

    if (canUpdate) {
      result.push({
        name: `Check Is Ready (${childCount} items)`,
        action: () => onCheckIsReadyForGroup(groupKeys),
        icon: '<span class="ag-icon ag-icon-tick" unselectable="on" role="presentation"></span>',
      });

      const groupForceBlocked = groupForceBlockedReason !== undefined;

      result.push({
        name: 'Force for Client (group)…',
        action: () => onForceClientForGroup(groupKeys),
        icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
        disabled: groupForceBlocked,
        ...(groupForceBlocked && { tooltip: groupForceBlockedReason }),
      });

      result.push({
        name: 'Force for Client Agent (group)…',
        action: () => onForceClientAgentForGroup(groupKeys),
        icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
        disabled: groupForceBlocked,
        ...(groupForceBlocked && { tooltip: groupForceBlockedReason }),
      });
    }

    if (canRestore) {
      result.push({
        name: `Restore Group (${childCount} items)`,
        subMenu: [
          {
            name: 'By Scheme',
            action: () => onRestoreGroupByScheme(groupKeys),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
          },
          {
            name: 'By Client',
            action: () => onRestoreGroupByClient(groupKeys),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
          },
          {
            name: 'By ClientAgent',
            action: () => onRestoreGroupByClientAgent(groupKeys),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
          },
        ],
        icon: '<span class="ag-icon ag-icon-group" unselectable="on" role="presentation"></span>',
      });
    }

    result.push('separator');
  }

  if (canUpdate) {
    result.push({
      name: 'Check Is Ready',
      action: () => onCheckExistsClick(),
      icon: '<span class="ag-icon ag-icon-tick" unselectable="on" role="presentation"></span>',
      disabled: !rowSelected,
    });
  }

  result.push('separator');

  if (canUpdate && rowSelected) {
    result.push({
      name: 'Update pending dependencies',
      subMenu: [
        {
          name: 'Abandon',
          action: () => onAbandonClick(),
          icon: '<span class="ag-icon ag-icon-cross" unselectable="on" role="presentation"></span>',
          disabled: !rowSelected,
        },
        {
          name: 'Force Client',
          action: () => onForceClientClick(),
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          disabled: !rowSelected || rowsSelectedMissingClient > 0,
          ...(rowsSelectedMissingClient > 0 && { tooltip: missingTargetTooltip(rowsSelectedMissingClient, 'Client') }),
        },
        {
          name: 'Force ClientAgent',
          action: () => onForceClientAgentClick(),
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          disabled: !rowSelected || rowsSelectedMissingClientAgent > 0,
          ...(rowsSelectedMissingClientAgent > 0 && { tooltip: missingTargetTooltip(rowsSelectedMissingClientAgent, 'ClientAgent') }),
        },
      ],
      icon: '<span class="ag-icon ag-icon-group" unselectable="on" role="presentation"></span>',
      disabled: !canUpdate,
    });

    result.push('separator');
  }

  if (canRestore) {
    if (rowSelectedADV) {
      result.push({
        name: 'Restore ADV',
        subMenu: [
          {
            name: 'By Scheme',
            action: () => onRestoreSchemePendingDependenciesClick('AssetDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedADV,
          },
          {
            name: 'By Client',
            action: () => onRestoreClientPendingDependenciesClick('AssetDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedADV,
          },
          {
            name: 'By ClientAgent',
            action: () => onRestoreClientAgentPendingDependenciesClick('AssetDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedADV,
          },
        ],
        icon: '<span class="ag-icon ag-icon-group" unselectable="on" role="presentation"></span>',
        disabled: !canRestore,
      });

      result.push('separator');
    }

    if (rowSelectedLDV) {
      result.push({
        name: 'Restore DV',
        subMenu: [
          {
            name: 'By Scheme',
            action: () => onRestoreSchemePendingDependenciesClick('LiabilityDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedLDV,
          },
          {
            name: 'By Client',
            action: () => onRestoreClientPendingDependenciesClick('LiabilityDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedLDV,
          },
          {
            name: 'By ClientAgent',
            action: () => onRestoreClientAgentPendingDependenciesClick('LiabilityDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedLDV,
          },
        ],
        icon: '<span class="ag-icon ag-icon-group" unselectable="on" role="presentation"></span>',
        disabled: !canRestore,
      });

      result.push('separator');
    }

    if (rowSelectedDM) {
      result.push({
        name: 'Restore DM',
        subMenu: [
          {
            name: 'By Scheme',
            action: () => onRestoreSchemePendingDependenciesClick('DailyMetric'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedDM,
          },
          {
            name: 'AssetDV By Scheme',
            action: () => onRestoreSchemePendingDependenciesClick('AssetDV'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedADVDep,
          },
          {
            name: 'By Client',
            action: () => onRestoreClientPendingDependenciesClick('DailyMetric'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedDM,
          },
          {
            name: 'By ClientAgent',
            action: () => onRestoreClientAgentPendingDependenciesClick('DailyMetric'),
            icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
            disabled: !rowSelectedDM,
          },
        ],
        icon: '<span class="ag-icon ag-icon-group" unselectable="on" role="presentation"></span>',
        disabled: !canRestore,
      });

      result.push('separator');
    }

    if (rowSelected) {
      result.push({
        name: 'Restore All By Environment',
        action: () => onRestoreEnvPendingDependenciesClick('All'),
        icon: '<span class="ag-icon ag-icon-linked" unselectable="on" role="presentation"></span>',
        disabled: !rowSelected && !canRestore,
      });

      result.push('separator');
    }
  }

  result.push('separator');

  if (params.node?.group) {
    result.push({
      name: `Export Group to CSV (${(params.node.data?.childCount as number) ?? params.node.allChildrenCount ?? 0} items)`,
      action: () => onExportGroupCsvClick(getGroupKeysFromNode(params.node!)),
      icon: '<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>',
    });
  }

  result.push({
    name: 'Export to CSV (All Rows)',
    action: () => onExportCsvClick(),
    icon: '<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>',
  });

  return result;
}
